import service from '.';

/**
 * CompanyAnalysisPageRes，公司分析列表
 */
export interface CompanyAnalysisPageRes {
    /**
     * 公司分析id
     */
    analysisId: string;
    /**
     * 公司分析名称
     */
    analysisName?: string;
    /**
     * 公司id
     */
    companyId?: string;
    /**
     * 公司名称
     */
    companyName?: string;
    /**
     * 创建时间
     */
    createTime?: Date;
    /**
     * 分析状态
     */
    status?: EAnalysisStatus;
    /**
     * 发布状态
     */
    publishStatus?: EAnalysisPublishStatus | 0 | 1;
    /**
     * 报告类型
     */
    reportCycle?: EAnalysisReportType;
    sourceFileUrl?: string;
    sourceFileName?: string;
    sourceFileType?: string;
    sourceFileSize?: number;
    previewFileUrl?: string;
    previewFileType?: string;
    [property: string]: any;
}

/**
 * 分析状态
 */
export enum EAnalysisStatus {
    Complete = "COMPLETE",
    Fail = "FAIL",
    Running = "RUNNING",
    Invalidated = "INVALIDATED",
    RetryWait = "RETRY_WAIT",
}

/**
 * 分析报告发布状态
 */
export enum EAnalysisPublishStatus {
    Unpublished = "UNPUBLISHED",
    Published = "PUBLISHED",
}

export enum EAnalysisReportType {
    Unset = "UNSET",
    Daily = "DAILY",
    Weekly = "WEEKLY",
    Monthly = "MONTHLY",
}

export interface ScheduledReportPackage {
    recordId: string;
    name: string;
    type: number;
    publishStatus: number;
}

export interface ScheduledReportCreate {
    companyId: string;
    taskName: string;
    startMode: 'IMMEDIATE' | 'AT_TIME';
    firstRunTime?: string | null;
    intervalDays: number;
    dataSource: 'COMPANY_REPORT' | 'CLIENT_INDUSTRY_BRAND' | 'CLIENT_DATA_DASHBOARD';
    timeRange: 'ALL' | 'LAST_7_DAYS' | 'LAST_30_DAYS' | 'LATEST_1_DAY';
    packageIds: string[];
    reportCycle?: EAnalysisReportType;
    reportAutoPublishToClient?: 'ENABLE' | 'DISABLE';
    reportAutoUpdateClientReport?: 'ENABLE' | 'DISABLE';
}

export type ScheduledExecutionStatus = 'WAITING' | 'RUNNING' | 'RETRY_WAIT' | 'SUCCESS' | 'NO_DATA' | 'FAILED' | 'INVALIDATED' | 'SKIPPED_EXPIRED';
export interface ScheduledReportPlan extends Pick<ScheduledReportCreate, 'taskName' | 'dataSource' | 'timeRange' | 'intervalDays' | 'reportCycle' | 'reportAutoPublishToClient' | 'reportAutoUpdateClientReport'> {
    planId: string;
    status: 'ENABLED' | 'PAUSED' | 'EXPIRED' | 'CANCELED';
    firstRunTime: string;
    nextRunTime: string;
    latestStatus?: ScheduledExecutionStatus;
    packages?: { recordId: string; name: string }[];
}
export interface ScheduledReportExecution {
    executionId: string;
    dueTime: string;
    status: ScheduledExecutionStatus;
    attemptCount: number;
    retryCount: number;
    nextRetryTime?: string;
    startedAt?: string;
    finishedAt?: string;
    analysisId?: string;
    failureMessage?: string;
}
export const analysisApi = {
    list: (data: { companyId: string; pageIndex: number; pageSize: number, search?: string }) => {
        return service.post<List<CompanyAnalysisPageRes>>('/company/analysis/page', data);
    },
    get: (analysisId: string) => {
        return service.get<CompanyAnalysisPageRes>('/company/analysis/detail', { params: { analysisId } });
    },
    delete: (data: { analysisIds: string[] }) => {
        return service.post('/company/analysis/delete', data);
    },
    update: (data: { analysisId: string; analysisName: string; reportCycle?: EAnalysisReportType; content: string }) => {
        return service.post('/company/analysis/update', data);
    },
    upload: (data: FormData) => {
        return service.post('/company/analysis/upload', data, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    },
    export: (data: { analysisIds: string[] }) => {
        return service.post('/company/analysis/download', data);
    },
    publish: (data: { analysisIds: string[] }) => {
        return service.post('/company/analysis/publish', data);
    },
    unpublish: (data: { analysisIds: string[] }) => {
        return service.post('/company/analysis/unpublish', data);
    },
    /* 未登录获取分析报告 */
    getWithoutAuth: (analysisId: string) => {
        return service.get<CompanyAnalysisPageRes>('/company/analysis/get', { params: { analysisId } });
    },
    scheduledPackages: (data: { companyId: string; pageIndex: number; pageSize: number }) =>
        service.post<{ records: ScheduledReportPackage[]; total: number }>('/company/analysis/scheduled/packages', data),
    createScheduled: (data: ScheduledReportCreate, requestKey: string) =>
        service.post('/company/analysis/scheduled/create', data, { headers: { 'Idempotency-Key': requestKey } }),
    updateScheduled: (data: Omit<ScheduledReportCreate, 'startMode'> & { planId: string; startMode?: ScheduledReportCreate['startMode'] }) =>
        service.post('/company/analysis/scheduled/update', data),
    pauseScheduled: (planId: string) =>
        service.post('/company/analysis/scheduled/pause', { planId }),
    resumeScheduled: (planId: string) =>
        service.post('/company/analysis/scheduled/resume', { planId }),
    deleteScheduled: (planId: string) =>
        service.post('/company/analysis/scheduled/delete', { planId }),
    scheduledPlans: (data: { companyId: string; pageIndex: number; pageSize: number }) =>
        service.post<List<ScheduledReportPlan>>('/company/analysis/scheduled/page', data),
    scheduledDetail: (planId: string) =>
        service.get<ScheduledReportPlan & { packageIds: string[] }>('/company/analysis/scheduled/detail', { params: { planId } }),
    scheduledExecutions: (data: { planId: string; pageIndex: number; pageSize: number }) =>
        service.post<List<ScheduledReportExecution>>('/company/analysis/scheduled/execution/page', data),
}
